'use client';

import { IntercomProvider, useIntercom } from 'react-use-intercom';

import { use, useEffect } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { env } from '@/env';
import { CurrentUserContext } from '@/context/CurrentUserContext';

export const INTERCOM_USER_HASH_FRAGMENT = graphql(`
  fragment intercomUserHashFragment on User {
    _id
    intercomHash {
      hash
    }
  }
`);

interface Props {
  children: React.ReactNode;
  intercomUser:
    DocumentType<typeof INTERCOM_USER_HASH_FRAGMENT> | null | undefined;
}

// Keep intercom up to date
const IntercomUpdater = ({
  userHash,
  userId,
}: {
  userHash?: string;
  userId?: string;
}) => {
  const { update } = useIntercom();

  useEffect(() => {
    if (!userHash && !userId) {
      return;
    }
    update({ userHash, userId, hideDefaultLauncher: false });
  }, [update, userHash, userId]);

  return null;
};

// autoBoot to get it going but quickly update to current user
export const Or2IntercomProvider = ({ children, intercomUser }: Props) => {
  const { isImpersonating } = use(CurrentUserContext);

  const isDevVm =
    env.NEXT_PUBLIC_NODE_ENV === 'development' && env.NEXT_PUBLIC_DEV_VM;

  const isIntercomDisabled = isDevVm || isImpersonating;

  const userId = intercomUser?._id || undefined;
  const userHash = intercomUser?.intercomHash?.hash || undefined;

  const bootProps =
    intercomUser && !isIntercomDisabled
      ? { userHash, userId }
      : { hideDefaultLauncher: true };

  return (
    <IntercomProvider
      appId={env.NEXT_PUBLIC_INTERCOM_APP_ID}
      autoBoot={!isIntercomDisabled}
      shouldInitialize={!isIntercomDisabled}
      autoBootProps={bootProps}
    >
      {!isIntercomDisabled && (
        <IntercomUpdater userHash={userHash} userId={userId} />
      )}
      {children}
    </IntercomProvider>
  );
};
